export const BROWSER_NOTIFICATIONS_KEY = 'notifications.browser'

export const BROWSER_NOTIFICATION_ICON = '/android-chrome-192x192.png'

export type BrowserNotificationPermission = 'unsupported' | 'default' | 'granted' | 'denied'

export function browserNotificationsSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window
}

export function notificationPermission(): BrowserNotificationPermission {
    if (!browserNotificationsSupported()) return 'unsupported'

    return Notification.permission as BrowserNotificationPermission
}

export function readBrowserNotificationsEnabled(): boolean {
    try {
        return localStorage.getItem(BROWSER_NOTIFICATIONS_KEY) === 'true'
    } catch {
        return false
    }
}

export function writeBrowserNotificationsEnabled(enabled: boolean): void {
    try {
        localStorage.setItem(BROWSER_NOTIFICATIONS_KEY, String(enabled))
    } catch {
        return
    }
}

const preferenceListeners = new Set<() => void>()

export function subscribeToBrowserNotifications(listener: () => void): () => void {
    preferenceListeners.add(listener)

    return () => {
        preferenceListeners.delete(listener)
    }
}

export function getBrowserNotificationsEnabled(): boolean {
    return readBrowserNotificationsEnabled()
}

export function setBrowserNotificationsEnabled(enabled: boolean): void {
    writeBrowserNotificationsEnabled(enabled)
    for (const listener of preferenceListeners) listener()
}

export async function requestBrowserNotificationPermission(): Promise<BrowserNotificationPermission> {
    if (!browserNotificationsSupported()) return 'unsupported'
    if (Notification.permission !== 'default') {
        return Notification.permission as BrowserNotificationPermission
    }

    try {
        const permission = await Notification.requestPermission()

        return permission as BrowserNotificationPermission
    } catch {
        return 'denied'
    }
}

export function isPageLooking(): boolean {
    if (typeof document === 'undefined') return false
    if (document.visibilityState !== 'visible') return false

    return typeof document.hasFocus === 'function' ? document.hasFocus() : true
}

export interface BrowserNotificationInput {
    title: string
    body?: string
    tag?: string
    onClick?: () => void
}

export function showBrowserNotification(input: BrowserNotificationInput): boolean {
    if (!browserNotificationsSupported()) return false
    if (Notification.permission !== 'granted') return false
    if (!readBrowserNotificationsEnabled()) return false
    if (!input.title) return false
    if (isPageLooking()) return false

    try {
        const notification = new Notification(input.title, {
            body: input.body ?? '',
            tag: input.tag,
            icon: BROWSER_NOTIFICATION_ICON,
        })
        notification.onclick = () => {
            window.focus()
            notification.close()
            input.onClick?.()
        }

        return true
    } catch {
        return false
    }
}

export interface UnreadRow {
    uid?: string
    unread_count?: number
}

export interface UnreadDiff<T> {
    arrivals: T[]
    baseline: Map<string, number>
}

export function diffUnreadRows<T extends UnreadRow>(
    baseline: ReadonlyMap<string, number>,
    rows: readonly T[],
    seeded: boolean,
): UnreadDiff<T> {
    const next = new Map<string, number>()
    const arrivals: T[] = []

    for (const row of rows) {
        const uid = row.uid
        if (!uid) continue
        const unread = row.unread_count ?? 0
        const previous = baseline.get(uid)
        next.set(uid, unread)
        if (!seeded) continue
        if (previous === undefined ? unread > 0 : unread > previous) arrivals.push(row)
    }

    return { arrivals, baseline: next }
}

export function firstMeaningfulLine(text: string | null | undefined, max = 160): string {
    const raw = (text ?? '').trim()
    if (!raw) return ''
    const line = raw.split(/\r?\n/, 1)[0].trim()

    return line.length > max ? `${line.slice(0, max - 1)}…` : line
}
